import { type FunctionComponent } from 'react';

import { Link } from '@/core/routing/components/Link';
import { isB2C, isUkPlatform } from '@/core/settings/utils';
import { Gtm } from '@/core/tracking/utils/Gtm';
import { LinkListItem } from '@/retention/Footer/components/LinkList/LinkListItem';
import { Accordion } from '@/retention/Footer/modules/Accordion/Accordion';
import {
  GTM_CLICK_ON_FOOTER_LINK,
  type LinkData,
} from '@/retention/Footer/tracking/gtmEvents';
import {
  footerAboutTitle,
  footerBestSellers,
  footerBestSellersHref,
  footerManoExpress,
  footerManoExpressHref,
  footerManoExpressHrefB2B,
  footerNavigationBrands,
  footerNavigationBrandsHref,
  footerSitemapAdvice,
  footerSitemapAdviceHref,
  footerSitemapCurrentOffers,
  footerSitemapCurrentOffersHref,
  footerSitemapInspirations,
  footerSitemapInspirationsHref,
  footerSitemapProductCategories,
  footerSitemapProductCategoriesHref,
  footerSitemapTopProducts,
  footerSitemapTopProductsHref,
} from '@/retention/Footer/translations';

import { LinkList } from '../../components/LinkList/LinkList';

interface LinkInfo {
  id: string;
  text: string;
  url: string;
}

const LINKS: LinkInfo[] = [
  {
    id: 'siteMapAdviceCta',
    text: footerSitemapAdvice,
    url: footerSitemapAdviceHref,
  },
  {
    id: 'siteMapMasslandingCta',
    text: footerSitemapProductCategories,
    url: footerSitemapProductCategoriesHref,
  },
  {
    id: 'siteMapTopProductsCta',
    text: footerSitemapTopProducts,
    url: footerSitemapTopProductsHref,
  },
  {
    id: 'brandListCta',
    text: footerNavigationBrands,
    url: footerNavigationBrandsHref,
  },
  {
    id: 'siteMapCurrentOffersCta',
    text: footerSitemapCurrentOffers,
    url: footerSitemapCurrentOffersHref,
  },
  {
    id: 'siteMapInspirationsCta',
    text: footerSitemapInspirations,
    url: footerSitemapInspirationsHref,
  },
  {
    id: 'BestSellersCta',
    text: footerBestSellers,
    url: footerBestSellersHref,
  },
  isUkPlatform()
    ? undefined
    : {
        id: 'ManoExpressCta',
        text: footerManoExpress,
        url: isB2C() ? footerManoExpressHref : footerManoExpressHrefB2B,
      },
].filter((item) => !!item);

const handleTracking = (data: Omit<LinkData, 'context'>) => () =>
  Gtm.push(GTM_CLICK_ON_FOOTER_LINK({ context: 'About us', ...data }));

export const About: FunctionComponent = () => (
  <div
    className="border-t border-foundation-inverted pt-m desktop:border-t-0
      desktop:pt-0"
  >
    <Accordion title={footerAboutTitle}>
      <LinkList>
        {LINKS.map(({ id, text, url }, index) => (
          <LinkListItem
            key={text}
            className="py-xs"
            anchorTag={Link}
            color="dark"
            data-testid={id}
            href={url}
            onClick={handleTracking({ position: index + 1, text, url })}
          >
            {text}
          </LinkListItem>
        ))}
      </LinkList>
    </Accordion>
  </div>
);

About.displayName = 'About';
